import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { apiFetch, apiUploadFormData } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { FilePicker } from "@/components/ui/FilePicker";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { listCommissionStocking, listPosReports, money } from "./commissionsApi";

type PosReportRow = Awaited<ReturnType<typeof listPosReports>>["rows"][number];
type PosReportLineRow = Record<string, unknown>;
type StockingInventoryRow = Awaited<ReturnType<typeof listCommissionStocking>>["rows"][number];

function PosReportsTable({
  rows,
  loading,
  error,
  onReview,
  onConfirm,
}: {
  rows: PosReportRow[];
  loading: boolean;
  error: string | null;
  onReview: (id: number) => void;
  onConfirm: (id: number) => void;
}) {
  const columns: HubDataTableColumn<PosReportRow>[] = [
    {
      id: "id",
      header: "Id",
      overflow: "nowrap",
      sortValue: (row) => Number(row.id),
      cell: (row) => String(row.id),
    },
    {
      id: "distributor",
      header: "Distributor",
      sortValue: (row) => String(row.distributor),
      cell: (row) => String(row.distributor),
    },
    {
      id: "report_month",
      header: "Month",
      overflow: "nowrap",
      sortValue: (row) => String(row.report_month ?? ""),
      cell: (row) => String(row.report_month ?? "—"),
    },
    {
      id: "status",
      header: "Status",
      overflow: "nowrap",
      sortValue: (row) => String(row.status),
      cell: (row) => String(row.status),
    },
    {
      id: "line_count",
      header: "Lines",
      align: "right",
      overflow: "nowrap",
      sortValue: (row) => Number(row.line_count),
      cell: (row) => String(row.line_count),
    },
    {
      id: "actions",
      header: "",
      sortValue: () => "",
      cell: (row) => {
        const id = Number(row.id);
        return (
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={() => onReview(id)}>
              Review
            </Button>
            {row.status === "needs_review" ? (
              <Button type="button" size="sm" variant="secondary" onClick={() => onConfirm(id)}>
                Confirm
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <HubDataTable
      title="POS reports"
      columns={columns}
      rows={rows}
      rowKey={(row) => Number(row.id)}
      loading={loading}
      loadingMessage="Loading POS reports…"
      error={error}
      empty="No POS reports uploaded yet."
    />
  );
}

const REPORT_LINE_COLUMNS: HubDataTableColumn<PosReportLineRow>[] = [
  {
    id: "end_user",
    header: "End user",
    sortValue: (row) => String(row.end_user_name ?? ""),
    cell: (row) => String(row.end_user_name ?? "—"),
  },
  {
    id: "sku",
    header: "SKU",
    overflow: "nowrap",
    sortValue: (row) => String(row.sku_normalized ?? row.sku_raw ?? ""),
    cell: (row) => String(row.sku_normalized ?? row.sku_raw ?? "—"),
  },
  {
    id: "quantity",
    header: "Qty",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => Number(row.quantity ?? 0),
    cell: (row) => String(row.quantity ?? "—"),
  },
  {
    id: "match_status",
    header: "Status",
    overflow: "nowrap",
    sortValue: (row) => String(row.match_status),
    cell: (row) => String(row.match_status),
  },
  {
    id: "invoice",
    header: "Invoice",
    overflow: "nowrap",
    sortValue: (row) => String(row.matched_invoice_mv_id ?? ""),
    cell: (row) => String(row.matched_invoice_mv_id ?? "—"),
  },
  {
    id: "note",
    header: "Note",
    overflow: "truncate",
    sortValue: (row) => String(row.split_note ?? ""),
    cell: (row) => String(row.split_note ?? "—"),
  },
];

const STOCKING_INVENTORY_COLUMNS: HubDataTableColumn<StockingInventoryRow>[] = [
  {
    id: "invoice",
    header: "Invoice",
    overflow: "nowrap",
    sortValue: (row) => String(row.invoice_number),
    cell: (row) => String(row.invoice_number),
  },
  {
    id: "sku",
    header: "SKU",
    overflow: "nowrap",
    sortValue: (row) => String(row.sku),
    cell: (row) => String(row.sku),
  },
  {
    id: "quantity",
    header: "Qty",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => Number(row.quantity),
    cell: (row) => String(row.quantity),
  },
  {
    id: "resold",
    header: "Resold",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => Number(row.resold_quantity),
    cell: (row) => String(row.resold_quantity),
  },
  {
    id: "remaining",
    header: "Remaining",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => Number(row.remaining_quantity),
    cell: (row) => String(row.remaining_quantity),
  },
  {
    id: "remaining_value",
    header: "Remaining value",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => Number(row.remaining_value),
    cell: (row) => money(row.remaining_value as number),
  },
];

export function CommissionStockingPage() {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [distributor, setDistributor] = useState("Tri-State");
  const [reportMonth, setReportMonth] = useState("");
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);

  const stockingQuery = useQuery({
    queryKey: ["commissions", "stocking"],
    queryFn: listCommissionStocking,
  });
  const reportsQuery = useQuery({
    queryKey: ["commissions", "pos-reports"],
    queryFn: listPosReports,
  });
  const reportDetailQuery = useQuery({
    queryKey: ["commissions", "pos-report", selectedReportId],
    queryFn: () =>
      apiFetch<{ row: Record<string, unknown>; lines: Array<Record<string, unknown>> }>(
        `/api/commissions/pos-reports/${selectedReportId}`,
      ),
    enabled: selectedReportId != null,
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const file = fileRef.current?.files?.[0];
      if (file == null) {
        throw new Error("Choose a POS report file first.");
      }
      const body = new FormData();
      body.append("file", file);
      body.append("distributor", distributor);
      if (reportMonth.trim() !== "") {
        body.append("report_month", `${reportMonth}-01`);
      }
      return apiUploadFormData<{ id: number; lineCount: number }>("/api/commissions/pos-reports", body);
    },
    onSuccess: (result) => {
      setSelectedReportId(result.id);
      void queryClient.invalidateQueries({ queryKey: ["commissions", "pos-reports"] });
      void queryClient.invalidateQueries({ queryKey: ["commissions", "stocking"] });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/commissions/pos-reports/${id}/confirm`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["commissions", "pos-reports"] });
      void queryClient.invalidateQueries({ queryKey: ["commissions", "stocking"] });
      void queryClient.invalidateQueries({ queryKey: ["commissions", "pos-report", selectedReportId] });
    },
  });

  const reportLines = reportDetailQuery.data?.lines ?? [];
  const stockingRows = stockingQuery.data?.rows ?? [];

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint="Upload distributor POS reports (xlsx/csv). Confirm after matching stocked inventory.">
        Regional stocking
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Upload POS report</CardTitle>
        </CardHeader>
        <CardBody className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="distributor">Distributor</Label>
            <Input id="distributor" value={distributor} onChange={(event) => setDistributor(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="report-month">Report month</Label>
            <Input
              id="report-month"
              type="month"
              value={reportMonth}
              onChange={(event) => setReportMonth(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="pos-file">File</Label>
            <FilePicker id="pos-file" accept=".csv,.tsv,.txt,.xlsx" ref={fileRef} />
          </div>
          <Button type="button" onClick={() => uploadMutation.mutate()} disabled={uploadMutation.isPending}>
            Upload and match
          </Button>
          {uploadMutation.isError ? (
            <p className="text-sm text-red-700">
              {uploadMutation.error instanceof Error ? uploadMutation.error.message : "Upload failed."}
            </p>
          ) : null}
        </CardBody>
      </Card>

      <PosReportsTable
        rows={reportsQuery.data?.rows ?? []}
        loading={reportsQuery.isPending}
        error={reportsQuery.isError ? "Could not load POS reports." : null}
        onReview={setSelectedReportId}
        onConfirm={(id) => confirmMutation.mutate(id)}
      />

      {reportDetailQuery.data != null ? (
        <HubDataTable
          title="Report lines"
          columns={REPORT_LINE_COLUMNS}
          rows={reportLines}
          rowKey={(row) => String(row.id)}
          loading={reportDetailQuery.isPending}
          loadingMessage="Loading report lines…"
          error={reportDetailQuery.isError ? "Could not load report lines." : null}
          empty="No lines on this report."
        />
      ) : null}

      <HubDataTable
        title={`Remaining stocked inventory (${money(stockingQuery.data?.totalRemainingValue ?? 0)})`}
        columns={STOCKING_INVENTORY_COLUMNS}
        rows={stockingRows}
        rowKey={(row) => String(row.id)}
        loading={stockingQuery.isPending}
        loadingMessage="Loading stocked inventory…"
        error={stockingQuery.isError ? "Could not load stocked inventory." : null}
        empty="No remaining stocked inventory."
      />
    </div>
  );
}
